import {ComponentProps, FC, ReactNode} from 'react'
import {gql} from '@apollo/client'
import {Controller, FormProvider, SubmitHandler, useForm} from 'react-hook-form'
import {yup} from 'libs/yup'
import {yupResolver} from '@hookform/resolvers/yup'
import Button from 'jbc-front/components/Button'
import {useMutation, useQuery} from 'hooks/graphql'
import {EmployeeName} from 'pages/withAuth/adminPage/procedures/parts/EmployeeName'
import {ProcedureContext, ReportFormProps} from './ReportForm'
import {useConfirmUpgrade} from './useConfirmUpgrade'
import {FormErrors} from 'components/reactHookForm/FormErrors'
import {Panel, Body} from 'components/ui/Panel'
import {laborConsultantSchema, LaborConsultantSection} from './LaborConsultant'
import {Label} from 'components/forms/Label'
import {Radio, RadioContainer} from 'jbc-front/components/presenters/form/Radio'
import {Checkbox} from 'jbc-front/components/presenters/form/Checkbox'
import {Textarea} from 'jbc-front/components/presenters/form/Textarea'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {useDispatch} from 'react-redux'
import {asyncError} from 'store/actions/asyncError'
import styles from './R49Report.scss'
import {DateField} from 'components/reactHookForm/DateField'
import {dateFormat} from 'libs/formatter'

const UPDATE_R49_PARAM_SET = gql`
  mutation UpdateR49ParamSet($input: UpdateR49ParamSetInput!) {
    updateR49ParamSet(input: $input) {
      r49ParamSet {
        id
      }
    }
  }
`

const R49_REPORT = gql`
  query R49Report($procedureStatusId: ID!) {
    client {
      id
      procedureStatus(procedureStatusId: $procedureStatusId) {
        id
        procedureType
        employee {
          id
          staffCode
          displayFullName
          employeeDependents {
            id
            firstName
            lastName
            dependentSocialInsurance
          }
        }
        r49ParamSet {
          id
          employeeSelected
          issuanceElderlyBenefitCard
          returnElderlyBenefitCard
          reason
          notes
          submitOn
          needLaborConsultant
          laborConsultantCreateDate
          laborConsultantName
          laborConsultantAgentType
          laborConsultantPrefecture {
            id
          }
          laborConsultantPhoneNumber0
          laborConsultantPhoneNumber1
          laborConsultantPhoneNumber2
          version
          needUpgrade
          r49DependentParamSets {
            employeeDependentId
            issuanceElderlyBenefitCard
            returnElderlyBenefitCard
            reason
          }
        }
      }
    }
  }
`

type IssuanceElderlyBenefitCard = 'no_card' | 'need_card'
type ReturnElderlyBenefitCard = 'no_return' | 'need_return'

type EmployeeDependent = {
  id: string
  firstName: string
  lastName: string
  dependentSocialInsurance: boolean
}

interface R49DependentParamSetResult {
  employeeDependentId: string
  issuanceElderlyBenefitCard: IssuanceElderlyBenefitCard | null
  returnElderlyBenefitCard: ReturnElderlyBenefitCard | null
  reason: string | null
}

interface R49ReportQueryResult {
  client: {
    id: string
    procedureStatus: {
      id: string
      procedureType: string
      employee: {
        id: string
        staffCode: string
        displayFullName: string
        employeeDependents: EmployeeDependent[]
      }
      r49ParamSet: {
        id?: string
        employeeSelected: boolean
        issuanceElderlyBenefitCard: IssuanceElderlyBenefitCard | null
        returnElderlyBenefitCard: ReturnElderlyBenefitCard | null
        reason: string | null
        notes: string | null
        submitOn?: string | null
        needLaborConsultant: boolean
        laborConsultantCreateDate?: string
        laborConsultantName?: string
        laborConsultantAgentType?: 'submission_agent' | 'office_agent'
        laborConsultantPrefecture?: {id: string}
        laborConsultantPhoneNumber0?: string
        laborConsultantPhoneNumber1?: string
        laborConsultantPhoneNumber2?: string
        version: number
        needUpgrade: boolean
        r49DependentParamSets: R49DependentParamSetResult[]
      }
    }
  }
}

interface R49ReportQueryVariables {
  procedureStatusId: string
}

interface UpdateR49ParamSetQueryVariables {
  input: {
    procedureStatusId: string
    attributes: {
      employeeSelected?: boolean | null
      issuanceElderlyBenefitCard?: IssuanceElderlyBenefitCard | null
      returnElderlyBenefitCard?: ReturnElderlyBenefitCard | null
      reason?: string | null
      notes?: string | null
      submitOn?: string | null
      needLaborConsultant?: boolean | null
      laborConsultantCreateDate?: string | null
      laborConsultantName?: string | null
      laborConsultantAgentType?: 'submission_agent' | 'office_agent' | null
      laborConsultantPrefectureId?: string | null
      laborConsultantPhoneNumber0?: string | null
      laborConsultantPhoneNumber1?: string | null
      laborConsultantPhoneNumber2?: string | null
      version?: number
      r49DependentParamSets?:
        | {
            employeeDependentId: string
            issuanceElderlyBenefitCard?: IssuanceElderlyBenefitCard | null
            returnElderlyBenefitCard?: ReturnElderlyBenefitCard | null
            reason?: string | null
          }[]
        | null
    }
  }
}

interface R49DependentFormItem {
  dependentId: string
  selected: boolean
  issuanceElderlyBenefitCard: IssuanceElderlyBenefitCard
  returnElderlyBenefitCard: ReturnElderlyBenefitCard
  reason: string
}

const MAX_LINES = 5

const limitedTextSchema = (label: string) => yup.string().notRequired().max(255).maxLines(MAX_LINES).label(label)

const schema = yup.object({
  employeeSelected: yup.boolean().notRequired(),
  issuanceElderlyBenefitCard: yup.mixed<IssuanceElderlyBenefitCard>().notRequired(),
  returnElderlyBenefitCard: yup.mixed<ReturnElderlyBenefitCard>().notRequired(),
  reason: limitedTextSchema('資格確認書を返納できない理由'),
  notes: limitedTextSchema('備考'),
  submitOn: yup.string().notRequired().dateFormat().label('提出年月日'),
  laborConsultantForm: laborConsultantSchema.required(),
  r49DependentParamSets: yup
    .array()
    .of(
      yup.object({
        dependentId: yup.string().required(),
        selected: yup.boolean().notRequired(),
        issuanceElderlyBenefitCard: yup.mixed<IssuanceElderlyBenefitCard>().notRequired(),
        returnElderlyBenefitCard: yup.mixed<ReturnElderlyBenefitCard>().notRequired(),
        reason: limitedTextSchema('資格確認書を返納できない理由'),
      })
    )
    .required()
    .default([]),
})

type Schema = yup.InferType<typeof schema>

const makeDefaultValues = (ps: R49ReportQueryResult['client']['procedureStatus']): Schema => {
  const r49 = ps.r49ParamSet

  const socialInsuranceDependents = ps.employee.employeeDependents.filter((d) => d.dependentSocialInsurance)

  const r49DependentParamSets: R49DependentFormItem[] = socialInsuranceDependents.map((dependent) => {
    const saved = r49.r49DependentParamSets.find((d) => d.employeeDependentId === dependent.id)
    if (saved) {
      return {
        dependentId: dependent.id,
        selected: true,
        issuanceElderlyBenefitCard: saved.issuanceElderlyBenefitCard || 'no_card',
        returnElderlyBenefitCard: saved.returnElderlyBenefitCard || 'no_return',
        reason: saved.reason || '',
      }
    }
    return {
      dependentId: dependent.id,
      selected: false,
      issuanceElderlyBenefitCard: 'no_card',
      returnElderlyBenefitCard: 'no_return',
      reason: '',
    }
  })

  return {
    employeeSelected: r49.employeeSelected,
    issuanceElderlyBenefitCard: r49.issuanceElderlyBenefitCard || 'no_card',
    returnElderlyBenefitCard: r49.returnElderlyBenefitCard || 'no_return',
    reason: r49.reason || '',
    notes: r49.notes || '',
    submitOn: r49.submitOn ? dateFormat(r49.submitOn, 'YYYY/MM/DD') : '',
    laborConsultantForm: {
      needLaborConsultant: r49.needLaborConsultant,
      laborConsultantCreateDate: r49.laborConsultantCreateDate
        ? dateFormat(r49.laborConsultantCreateDate, 'YYYY/MM/DD')
        : '',
      laborConsultantName: r49.laborConsultantName || '',
      laborConsultantAgentType: r49.laborConsultantAgentType || undefined,
      laborConsultantPrefectureId: r49.laborConsultantPrefecture?.id || undefined,
      laborConsultantPhoneNumber0: r49.laborConsultantPhoneNumber0 || '',
      laborConsultantPhoneNumber1: r49.laborConsultantPhoneNumber1 || '',
      laborConsultantPhoneNumber2: r49.laborConsultantPhoneNumber2 || '',
    },
    r49DependentParamSets,
  }
}

const issuanceElderlyBenefitCardOptions = [
  {value: 'no_card', label: 'なし'},
  {value: 'need_card', label: 'あり'},
] as const

const returnElderlyBenefitCardOptions = [
  {value: 'no_return', label: 'なし'},
  {value: 'need_return', label: 'あり'},
] as const

interface ElderlyBenefitCardFieldsProps {
  getIssuanceRadioProps: (value: IssuanceElderlyBenefitCard) => Omit<ComponentProps<typeof Radio>, 'children'>
  getReturnRadioProps: (value: ReturnElderlyBenefitCard) => Omit<ComponentProps<typeof Radio>, 'children'>
}

const ElderlyBenefitCardFields: FC<ElderlyBenefitCardFieldsProps> = ({getIssuanceRadioProps, getReturnRadioProps}) => (
  <>
    <div className={styles.field}>
      <Label>高齢受給者証の交付</Label>
      <RadioContainer>
        {issuanceElderlyBenefitCardOptions.map(({value, label}) => (
          <Radio key={value} {...getIssuanceRadioProps(value)}>
            {label}
          </Radio>
        ))}
      </RadioContainer>
    </div>
    <div className={styles.field}>
      <Label>高齢受給者証の返納</Label>
      <RadioContainer>
        {returnElderlyBenefitCardOptions.map(({value, label}) => (
          <Radio key={value} {...getReturnRadioProps(value)}>
            {label}
          </Radio>
        ))}
      </RadioContainer>
    </div>
  </>
)

interface FormProps {
  procedureStatus: R49ReportQueryResult['client']['procedureStatus']
  submitText: string
  onSubmit: SubmitHandler<Schema>
  procedureContext: ProcedureContext
  comments?: ReactNode
  renderReject?: ReportFormProps['renderReject']
}

const Form: FC<FormProps> = ({procedureStatus, onSubmit, procedureContext, submitText, renderReject, comments}) => {
  const methods = useForm<Schema>({
    defaultValues: makeDefaultValues(procedureStatus),
    resolver: yupResolver(schema),
    mode: 'onTouched',
    shouldFocusError: false,
    context: {procedureContext},
  })
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: {errors, isSubmitting},
  } = methods

  const handleSubmitError = () => {
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  const {confirmUpgrade, confirmUpgradeModal} = useConfirmUpgrade(
    procedureStatus.r49ParamSet.needUpgrade,
    handleSubmit(onSubmit, handleSubmitError)
  )

  const socialInsuranceDependents = procedureStatus.employee.employeeDependents.filter(
    (d) => d.dependentSocialInsurance
  )
  const employeeSelected = watch('employeeSelected')

  const ensureDependentItems = (value: R49DependentFormItem[]): R49DependentFormItem[] =>
    socialInsuranceDependents.map(
      (dependent) =>
        value.find((item) => item.dependentId === dependent.id) ?? {
          dependentId: dependent.id,
          selected: false,
          issuanceElderlyBenefitCard: 'no_card',
          returnElderlyBenefitCard: 'no_return',
          reason: '',
        }
    )

  return (
    <FormProvider {...methods}>
      <div className="basic_information l-wrap-xs">
        <FormErrors errors={errors} isSubmitting={isSubmitting} />
        {comments}

        <Panel>
          <Body className={styles.formBody}>
            <div className={styles.field}>
              <Label>回収不能の対象者選択</Label>
              <Checkbox {...register('employeeSelected')}>{procedureStatus.employee.displayFullName}</Checkbox>
            </div>

            <ElderlyBenefitCardFields
              getIssuanceRadioProps={(value) => ({
                ...register('issuanceElderlyBenefitCard'),
                value,
                disabled: !employeeSelected,
              })}
              getReturnRadioProps={(value) => ({
                ...register('returnElderlyBenefitCard'),
                value,
                disabled: !employeeSelected,
              })}
            />

            <div className={styles.field}>
              <Label>資格確認書を返納できない理由</Label>
              <Textarea rows={7} {...register('reason')} isError={!!errors.reason} disabled={!employeeSelected} />
              {errors.reason?.message && <ErrorMessage>{errors.reason.message}</ErrorMessage>}
            </div>

            {socialInsuranceDependents.length > 0 && (
              <Controller
                control={control}
                name="r49DependentParamSets"
                render={({field: {value = [], onChange, onBlur}}) => {
                  const items = ensureDependentItems(value as R49DependentFormItem[])

                  const updateItem = (dependentId: string, patch: Partial<R49DependentFormItem>) => {
                    const nextValue = items.map((item) =>
                      item.dependentId === dependentId ? {...item, ...patch} : item
                    )
                    onChange(nextValue)
                  }

                  return (
                    <>
                      {socialInsuranceDependents.map((dependent, index) => {
                        const item = items.find((i) => i.dependentId === dependent.id)!
                        const dependentReasonError = errors.r49DependentParamSets?.[index]?.reason

                        return (
                          <div key={dependent.id} className={styles.dependentFieldGroup}>
                            <Checkbox
                              checked={item.selected}
                              onBlur={onBlur}
                              onChange={(event) => {
                                updateItem(dependent.id, {selected: event.target.checked})
                              }}
                            >
                              {dependent.lastName} {dependent.firstName}
                            </Checkbox>

                            <ElderlyBenefitCardFields
                              getIssuanceRadioProps={(value) => ({
                                checked: item.issuanceElderlyBenefitCard === value,
                                onChange: () => updateItem(dependent.id, {issuanceElderlyBenefitCard: value}),
                                onBlur,
                                disabled: !item.selected,
                              })}
                              getReturnRadioProps={(value) => ({
                                checked: item.returnElderlyBenefitCard === value,
                                onChange: () => updateItem(dependent.id, {returnElderlyBenefitCard: value}),
                                onBlur,
                                disabled: !item.selected,
                              })}
                            />

                            <div className={styles.field}>
                              <Label>資格確認書を返納できない理由</Label>
                              <Textarea
                                rows={7}
                                value={item.reason}
                                onChange={(e) => updateItem(dependent.id, {reason: e.target.value})}
                                onBlur={onBlur}
                                isError={!!dependentReasonError}
                                disabled={!item.selected}
                              />
                              {item.selected && dependentReasonError?.message && (
                                <ErrorMessage>{dependentReasonError?.message}</ErrorMessage>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </>
                  )
                }}
              />
            )}

            <div className={styles.field}>
              <Label>備考</Label>
              <Textarea rows={7} {...register('notes')} isError={!!errors.notes} />
              {errors.notes?.message && <ErrorMessage>{errors.notes.message}</ErrorMessage>}
            </div>

            <div className={styles.field}>
              <DateField name="submitOn" label="提出年月日" error={errors?.submitOn?.message} />
            </div>

            <LaborConsultantSection
              procedureStatusId={procedureStatus.id}
              reportName="R49"
              prefix="laborConsultantForm."
            />
          </Body>
        </Panel>

        <div className="u-ta-c u-mt30">
          {renderReject?.({isSubmitting})}
          <Button primary onClick={confirmUpgrade} disabled={isSubmitting}>
            {submitText}
          </Button>
        </div>
      </div>

      {confirmUpgradeModal}
    </FormProvider>
  )
}

export const R49Report: FC<ReportFormProps> = ({
  procedureStatusId,
  submitText,
  afterSubmit,
  procedureContext,
  comments,
  renderReject,
}) => {
  const dispatch = useDispatch()
  const {data, loading} = useQuery<R49ReportQueryResult, R49ReportQueryVariables>(R49_REPORT, {
    variables: {procedureStatusId},
    fetchPolicy: 'network-only',
  })

  const [updateR49ParamSet] = useMutation<unknown, UpdateR49ParamSetQueryVariables>(UPDATE_R49_PARAM_SET)

  if (loading || !data?.client?.procedureStatus) {
    return null
  }

  const procedureStatus = data.client.procedureStatus

  const onSubmit: SubmitHandler<Schema> = async (formData) => {
    const blankToNull = (value: string | undefined | null) => (value ? value : null)

    try {
      await updateR49ParamSet({
        variables: {
          input: {
            procedureStatusId,
            attributes: {
              employeeSelected: formData.employeeSelected,
              issuanceElderlyBenefitCard: formData.issuanceElderlyBenefitCard || null,
              returnElderlyBenefitCard: formData.returnElderlyBenefitCard || null,
              reason: blankToNull(formData.reason),
              notes: blankToNull(formData.notes),
              submitOn: blankToNull(formData.submitOn),
              needLaborConsultant: !!formData.laborConsultantForm.needLaborConsultant,
              laborConsultantCreateDate: blankToNull(formData.laborConsultantForm.laborConsultantCreateDate),
              laborConsultantName: formData.laborConsultantForm.laborConsultantName,
              laborConsultantAgentType: formData.laborConsultantForm.laborConsultantAgentType,
              laborConsultantPrefectureId: formData.laborConsultantForm.laborConsultantPrefectureId,
              laborConsultantPhoneNumber0: formData.laborConsultantForm.laborConsultantPhoneNumber0,
              laborConsultantPhoneNumber1: formData.laborConsultantForm.laborConsultantPhoneNumber1,
              laborConsultantPhoneNumber2: formData.laborConsultantForm.laborConsultantPhoneNumber2,
              version: procedureStatus.r49ParamSet.version,
              r49DependentParamSets:
                (formData.r49DependentParamSets as R49DependentFormItem[])
                  ?.filter((item) => item.selected)
                  .map((item) => ({
                    employeeDependentId: item.dependentId,
                    issuanceElderlyBenefitCard: item.issuanceElderlyBenefitCard || null,
                    returnElderlyBenefitCard: item.returnElderlyBenefitCard || null,
                    reason: blankToNull(item.reason),
                  })) || [],
            },
          },
        },
      })
    } catch (err) {
      dispatch(asyncError(err))
      return
    }

    return await afterSubmit()
  }

  return (
    <>
      <div className="l-title-wrap">
        <h1 className="m-title-main">健康保険資格確認書回収不能届</h1>
        <EmployeeName employee={procedureStatus.employee} />
      </div>
      <Form
        procedureStatus={procedureStatus}
        submitText={submitText}
        onSubmit={onSubmit}
        procedureContext={procedureContext}
        comments={comments}
        renderReject={renderReject}
      />
    </>
  )
}
